import once from 'lodash/once'
import { chunks } from '../events'
import { DEFAULT_STYLING } from 'app/views/icons/defaults'
import { isDuotoneFamily } from 'app/releases'

const SUBSCRIPTION_TIMEOUT = 2000

export const scrollBehavior = (to, from, savedPosition) => {
  if (!savedPosition) return null

  return new Promise((resolve) => {
    const r = once(resolve)

    if (to.name === from.name) {
      r(false)
    } else {
      chunks.subscribeOnceLoaded(() => {
        r(savedPosition)
      })

      setTimeout(r, SUBSCRIPTION_TIMEOUT)
    }
  })
}

export const stylingToQuery = (styling) => {
  const val = { ...styling }

  const routeProperties = {}

  for (const key in val) {
    if (!val[key]) {
      delete val[key]
    }
  }

  if (val.family) {
    routeProperties.f = val.family
  }

  if (val.style) {
    routeProperties.s = val.style
  }

  if (val.animation && val.rotation) {
    delete val.rotation
  }

  if (val.animation) {
    routeProperties.an = val.animation
  }

  if (val.size) {
    routeProperties.sz = val.size
  }

  if (val.rotation) {
    routeProperties.rt = val.rotation
  }

  if (val.colorPrimary && val.colorPrimary !== DEFAULT_STYLING.colorPrimary) {
    routeProperties.pc = val.colorPrimary
  }

  if (val.colorSecondary && val.colorSecondary !== DEFAULT_STYLING.colorSecondary) {
    routeProperties.sc = val.colorSecondary
  } else {
    delete routeProperties.sc
  }

  if (val.opacityPrimary && val.opacityPrimary !== DEFAULT_STYLING.opacityPrimary) {
    routeProperties.po = val.opacityPrimary
  } else {
    delete routeProperties.po
  }

  if (val.opacityPrimary !== DEFAULT_STYLING.opacityPrimary && val.colorPrimary === DEFAULT_STYLING.colorPrimary) {
    routeProperties.pc = val.colorPrimary
  }

  if (val.opacitySecondary && val.opacitySecondary !== DEFAULT_STYLING.opacitySecondary) {
    routeProperties.so = val.opacitySecondary
  } else {
    delete routeProperties.so
  }

  if (val.opacitySecondary !== DEFAULT_STYLING.opacitySecondary && val.colorSecondary === DEFAULT_STYLING.colorSecondary) {
    routeProperties.sc = val.colorSecondary
  }

  if (!isDuotoneFamily(val.family) && !val.style === 'duotone') {
    delete routeProperties.sc
    delete routeProperties.po
    delete routeProperties.so
  }

  return routeProperties
}

function accessibilityLinks(version) {
  return [
    {
      label: 'Accessibility',
      path: version === 5 ? '/web/other-topics/accessibility' : '/web/dig-deeper/accessibility'
    }
  ]
}

function duotoneLinks(version) {
  const label = 'Duotone Icons'

  let path = version === 5 ? '/web/style/duotone-icons' : '/web/style/duotone'

  return [{ label, path }]
}

function styleLinks(version) {
  const label = 'Styling Icons'

  let path = version === 5 ? '/web/style/' : '/web/style/styling'

  return [{ label, path }]
}

function wideCustomIconLinks(version) {
  return [
    {
      label: 'Wide Custom Icons',
      path: '/web/add-icons/upload-icons/prep-icons',
      hash: '#wide-custom-icons-and-rendering-behavior'
    }
  ]
}

export const allLearnAboutLinks = ({ version, links, selectedStyle, hasCustomStyling, isWideCustomIcon }) => {
  let combinedLinks = [...accessibilityLinks(version), ...links]

  if (selectedStyle === 'duotone') {
    combinedLinks = [...combinedLinks, ...duotoneLinks(version)]
  }

  if (hasCustomStyling) {
    combinedLinks = [...combinedLinks, ...styleLinks(version)]
  }

  if (isWideCustomIcon) {
    combinedLinks = [...combinedLinks, ...wideCustomIconLinks(version)]
  }

  return combinedLinks
}

// Web Awesome documentation links
export const WEB_AWESOME_ICON_DOCS_URL = 'https://webawesome.com/docs/components/icon/'

const createWebAwesomeLink = (label, hash) => ({
  label,
  path: WEB_AWESOME_ICON_DOCS_URL,
  hash: `#${hash}`,
  external: true
})

export const webAwesomeLearnAboutLinks = ({ links, isDuotone, hasCustomSize, hasCustomColor, hasRotationOrFlip, hasAnimation, isWideCustomIcon }) => {
  let combinedLinks = [createWebAwesomeLink('Accessibility', 'labels'), ...links]

  if (isDuotone) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Duotone', 'duotone')]
  }

  if (hasCustomSize) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Sizing', 'sizing')]
  }

  if (hasCustomColor) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Coloring', 'colors')]
  }

  if (hasRotationOrFlip) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Rotating', 'rotating-and-flipping')]
  }

  if (hasAnimation) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Animating', 'animating')]
  }

  if (isWideCustomIcon) {
    combinedLinks = [...combinedLinks, createWebAwesomeLink('Auto Width', 'auto-width')]
  }

  return combinedLinks
}
